import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import useHrIntegrations from "../../../api/hooks/useHrIntegrations";
import { adminPermissionKeys } from "../../../api/queryKeys";
import ConfirmationDialog from "../../../components/dialogs/ConfirmationDialog";
import AdminTypeOption from "../../../components/filterOption/AdminTypeOption";
import CircleOption from "../../../components/filterOption/CircleOption";
import ClearFiltersButton from "../../../components/filters/ClearFiltersButton";
import FilterMenu from "../../../components/filters/FilterMenu";
import SearchFilter from "../../../components/filters/SearchFilter";
import SearchInput from "../../../components/filters/SearchInput";
import LoadingIcon from "../../../components/LoadingIcon";
import AppPage from "../../../components/structure/AppPage";
import { PING_STATUS } from "../../../utils/constants";
import { searchObjectProperties } from "../../../utils/search";
import CustomLabel from "../../posture/frameworks/components/CustomLabel";
import { IntegrationCategory } from "../../stack/integrations/IntegrationsMapping";
import AdminPermissionsTable from "./components/AdminPermissionsTable";
import AdminUserDrawer from "./components/AdminUserDrawer";
import ExportAdminButton from "./components/ExportAdminButton";
import HrSystemInfoAlert from "./components/HrSystemInfoAlert";
import IntegrationFilter from "./components/IntegrationFilter";
import PingManagerModal from "./components/PingManagerModal";

const AdminPermissionsView = () => {
  const colors = useColors();
  const [appliedFilters, setAppliedFilters] = useState({
    tools: {},
    adminType: {},
    status: {},
    title: {},
    department: {},
    minNumber: 0,
    searchString: "",
    approvedBy: {},
  });
  const [adminUsersFiltered, setAdminUsersFiltered] = useState([]);
  const gridRef = useGridApiRef();
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [userToPing, setUserToPing] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toolsOptions, setToolsOptions] = useState([]);
  const [filterTools, setFilterTools] = useState({});
  const [openHrSystemInfo, setOpenHrSystemInfo] = useState(true);
  const [adminTypeCategories, setAdminTypeCategories] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    adminType: [],
    status: [...Object.values(PING_STATUS)],
    title: [],
    department: [],
    tools: [],
    approvedBy: [],
  });
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { fetchAllAdminUsers, trackEvent, approveUserPermissionsBatch } =
    useAPI();

  const handleConfirm = () => {
    trackEvent(
      "user approved permissions for multiple users" + selectedIdsToApprove
    );
    setIsLoading(true);
    approveUserPermissionsBatch(selectedIdsToApprove).then(() => {
      queryClient.invalidateQueries({ queryKey: adminPermissionKeys.all });
      setIsLoading(false);
      setOpenConfirmationDialog(false);
      setSelectedIdsToApprove([]);
    });
  };

  const { data: adminUsers, isLoading: adminsIsLoading } = useQuery({
    queryKey: adminPermissionKeys.all,
    queryFn: fetchAllAdminUsers,
    select: (data) =>
      data?.data.map((user, index) => ({ ...user, id: user.tool_email })),
  });

  const { isAnyHrIntegrated } = useHrIntegrations();
  const [selectedIdsToApprove, setSelectedIdsToApprove] = useState([]);

  useEffect(() => {
    if (adminUsers) {
      const toolsSet = new Set(adminUsers.map((user) => user.tool));
      const tools = Array.from(toolsSet);
      setToolsOptions(tools);

      const defaultTools = tools.reduce((acc, tool, index) => {
        acc[tool] = index === 0 ? true : false;
        return acc;
      }, {});

      setFilterTools((prev) => ({ ...defaultTools, ...prev }));

      if (selectedUser)
        setSelectedUser(
          adminUsers.find((user) => user.user_email === selectedUser.user_email)
        );
    }
  }, [adminUsers]);

  const circleColor = {
    Pending: colors.grey_500,
    Approved: colors.success_main,
    "Ping Sent": colors.info_dark,
  };
  const handleHeSystemConnect = () => {
    navigate("/inventory/integrations", {
      state: { filterIntegration: IntegrationCategory.HR },
    });
  };

  // filter the data based on the tools selected
  useEffect(() => {
    if (adminUsers) {
      //after the adminUsers are loaded, we need to filter the data based on the tools selected
      let newFilters = { ...filterTools };
      let message =
        "User applied filters on admin permission page: " +
        JSON.stringify(newFilters);
      trackEvent(message).then();

      let filtered = adminUsers;

      if (Object.keys(filterTools).length > 0) {
        const userAdmin_filter = Object.keys(filterTools).filter(
          (key) => filterTools[key] === true
        );
        if (userAdmin_filter.length > 0) {
          filtered = filtered.filter((adminUser) =>
            userAdmin_filter.includes(adminUser.tool)
          );
        }
      }
      setAdminUsersFiltered(filtered);
      const approvedBySet = new Set(
        filtered
          .filter((user) => user.ping_status.status === PING_STATUS.APPROVED)
          .map((user) => user.ping_status.approved_by)
      );
      const approvedBy = Array.from(approvedBySet);

      const adminTypesSet = new Set(
        filtered.flatMap((user) =>
          user.admin_permissions.map((entry) => entry.admin_type)
        )
      );
      const adminTypes = Array.from(adminTypesSet);

      const adminTypeToCategory = filtered.reduce((acc, user) => {
        user.admin_permissions.forEach((entry) => {
          acc[entry.admin_type] = entry.admin_category;
        });
        return acc;
      }, {});

      setAdminTypeCategories(adminTypeToCategory);

      const titleSet = new Set(
        filtered.map((user) => user.hr_info?.title).filter(Boolean)
      );
      const title = Array.from(titleSet);

      const departmentSet = new Set(
        filtered.map((user) => user.hr_info?.department).filter(Boolean)
      );
      const department = Array.from(departmentSet);

      setFilterOptions({
        adminType: ["All", ...adminTypes],
        status: [...Object.values(PING_STATUS)],
        title: ["All", ...title],
        department: ["All", ...department],
        tools: [...toolsOptions],
        approvedBy: ["All", ...approvedBy],
      });

      // Reset the appliedFilters for the second row when the first row selection changes
      setAppliedFilters((prev) => ({
        ...prev,
        adminType: {},
        status: {},
        title: {},
        department: {},
        approvedBy: {},
        searchString: "",
      }));
    }
  }, [filterTools]);

  useEffect(() => {
    if (adminUsers) {
      let newFilters = { ...appliedFilters };

      let message =
        "User applied filters on admin permission page: " +
        JSON.stringify(newFilters);
      trackEvent(message).then();
      let filtered = adminUsers;

      if (Object.keys(appliedFilters.title).length > 0) {
        const userAdmin_filter = Object.keys(appliedFilters.title).filter(
          (key) => appliedFilters.title[key] === true
        );
        if (userAdmin_filter.length > 0) {
          filtered = filtered.filter((adminUser) =>
            userAdmin_filter.includes(adminUser.hr_info?.title)
          );
        }
      }
      if (Object.keys(appliedFilters.department).length > 0) {
        const userAdmin_filter = Object.keys(appliedFilters.department).filter(
          (key) => appliedFilters.department[key] === true
        );
        if (userAdmin_filter.length > 0) {
          filtered = filtered.filter((adminUser) =>
            userAdmin_filter.includes(adminUser.hr_info?.department)
          );
        }
      }
      if (Object.keys(appliedFilters.status).length > 0) {
        const userAdmin_filter = Object.keys(appliedFilters.status).filter(
          (key) => appliedFilters.status[key] === true
        );
        if (userAdmin_filter.length > 0) {
          filtered = filtered.filter((adminUser) =>
            userAdmin_filter.includes(adminUser.ping_status.status)
          );
        }
      }
      if (Object.keys(appliedFilters.adminType).length > 0) {
        const userAdmin_filter = Object.keys(appliedFilters.adminType).filter(
          (key) => appliedFilters.adminType[key] === true
        );

        if (userAdmin_filter.length > 0) {
          filtered = filtered.filter((adminUser) => {
            let adminTypes = adminUser.admin_permissions.map(
              (admin) => admin.admin_type
            );
            return adminTypes.some((type) => userAdmin_filter.includes(type));
          });
        }
      }
      if (Object.keys(filterTools).length > 0) {
        const userAdmin_filter = Object.keys(filterTools).filter(
          (key) => filterTools[key] === true
        );
        if (userAdmin_filter.length > 0) {
          filtered = filtered.filter((adminUser) =>
            userAdmin_filter.includes(adminUser.tool)
          );
        }
      }
      if (Object.keys(appliedFilters.approvedBy).length > 0) {
        const userAdmin_filter = Object.keys(appliedFilters.approvedBy).filter(
          (key) => appliedFilters.approvedBy[key] === true
        );
        if (userAdmin_filter.length > 0) {
          filtered = filtered.filter((adminUser) =>
            userAdmin_filter.includes(adminUser.ping_status.approved_by)
          );
        }
      }

      if (appliedFilters.searchString) {
        const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
        filtered = filtered.filter((row) =>
          searchObjectProperties(row, lowerCaseSearchString)
        );
      }

      setAdminUsersFiltered(filtered);
    }
  }, [appliedFilters]);

  const onChipClick = (tool) => {
    const defaultTools = toolsOptions.reduce((acc, tool) => {
      acc[tool] = false;
      return acc;
    }, {});
    let toolSelected = true;
    setFilterTools({ ...defaultTools, [tool]: toolSelected });
  };

  return (
    <AppPage
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Privileged Access Overview
          <CustomLabel
            colorFamily={"primary"}
            severity={"dark"}
            labelType="soft"
            size="medium"
            text="BETA"
          />
        </Box>
      }
      subtitle={"Select a tool to view access permissions"}
    >
      {adminsIsLoading ? (
        <LoadingIcon />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Grid container gap={3}>
            {!isAnyHrIntegrated && (
              <HrSystemInfoAlert
                openHrSystemInfo={openHrSystemInfo}
                setOpenHrSystemInfo={setOpenHrSystemInfo}
                handleHeSystemConnect={handleHeSystemConnect}
                colors={colors}
              />
            )}
            <IntegrationFilter
              integrations={toolsOptions}
              onChipClick={onChipClick}
              filterTools={filterTools}
            />

            <Grid container justifyContent={"space-between"} gap={1}>
              <Grid item xs={12}>
                <Grid container spacing={1} alignItems={"center"}>
                  <Grid item>
                    <SearchFilter
                      filterName={"Admin Type"}
                      fieldName={"adminType"}
                      options={filterOptions.adminType.map((type) => {
                        if (type === "All") {
                          return {
                            name: type,
                            label: "All types",
                            displayName: type,
                          };
                        }
                        return {
                          name: type,
                          label: (
                            <AdminTypeOption
                              adminType={type}
                              category={adminTypeCategories[type]}
                            />
                          ),
                          displayName: type,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.adminType}
                    />
                  </Grid>
                  <Grid item>
                    <FilterMenu
                      filterName={"Status"}
                      fieldName={"status"}
                      options={filterOptions.status.map((status) => {
                        return {
                          name: status,
                          label: (
                            <CircleOption label={status} type={"status"} />
                          ),
                          displayName: status,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.status}
                    />
                  </Grid>
                  <Grid item>
                    <SearchFilter
                      filterName={"Approved By"}
                      fieldName={"approvedBy"}
                      options={filterOptions.approvedBy.map((approver) => {
                        if (approver === "All") {
                          return {
                            name: approver,
                            label: "All approvers",
                            displayName: approver,
                          };
                        }
                        return {
                          name: approver,
                          label: approver,
                          displayName: approver,
                        };
                      })}
                      appliedFilters={appliedFilters}
                      setAppliedFilters={setAppliedFilters}
                      fullOptions={filterOptions.approvedBy}
                    />
                  </Grid>
                  {isAnyHrIntegrated && (
                    <>
                      <Grid item>
                        <SearchFilter
                          filterName={"Title"}
                          fieldName={"title"}
                          options={filterOptions.title.map((title) => {
                            if (title === "All") {
                              return {
                                name: title,
                                label: "All titles",
                                displayName: title,
                              };
                            }
                            return {
                              name: title,
                              label: title,
                              displayName: title,
                            };
                          })}
                          appliedFilters={appliedFilters}
                          setAppliedFilters={setAppliedFilters}
                          fullOptions={filterOptions.title}
                        />
                      </Grid>
                      <Grid item>
                        <SearchFilter
                          filterName={"Department"}
                          fieldName={"department"}
                          options={filterOptions.department.map(
                            (department) => {
                              if (department === "All") {
                                return {
                                  name: department,
                                  label: "All departments",
                                  displayName: department,
                                };
                              }
                              return {
                                name: department,
                                label: department,
                                displayName: department,
                              };
                            }
                          )}
                          appliedFilters={appliedFilters}
                          setAppliedFilters={setAppliedFilters}
                          fullOptions={filterOptions.department}
                        />
                      </Grid>
                      <Grid item>
                        <ClearFiltersButton
                          appliedFilters={appliedFilters}
                          setAppliedFilters={setAppliedFilters}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={3}>
                <SearchInput
                  fieldName={"searchString"}
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                />
              </Grid>
              <Grid
                item
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                }}
              >
                <IconButton
                  sx={{
                    background: colors.common_white,
                    color: colors.primary_main,
                    height: "32px",
                    border:
                      "1px solid " +
                      (selectedIdsToApprove.length !== 0
                        ? colors.primary_main
                        : colors.grey_light),
                    borderRadius: "8px",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    "&:hover": {
                      background: colors.grey_8percent,
                      borderColor: colors.gray_main,
                    },
                  }}
                  onClick={() => setOpenConfirmationDialog(true)}
                  disabled={selectedIdsToApprove.length === 0}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="caption">Approve</Typography>
                    {isLoading ? (
                      <LoadingIcon size={10} />
                    ) : (
                      <DoneRoundedIcon
                        sx={{
                          width: "16px",
                        }}
                      />
                    )}
                  </Box>
                </IconButton>
                <ExportAdminButton adminUsers={adminUsers} />
              </Grid>
            </Grid>
          </Grid>
          <ConfirmationDialog
            openDialog={openConfirmationDialog}
            handleDialogClose={() => {
              setOpenConfirmationDialog(false);
            }}
            handleConfirm={handleConfirm}
            dialogTitle="Approval Confirmation"
            dialogContentFirstLine="Are you sure you want to approve the selected permissions?"
            isLoading={isLoading}
            isLoadingText="Approving..."
          />
          <AdminPermissionsTable
            adminUsers={adminUsersFiltered}
            gridRef={gridRef}
            setOpenModal={setOpenEmailModal}
            setUserToPing={setUserToPing}
            onRowClick={(row) => {
              trackEvent(
                'User click on "View" button to view user details'
              ).then();
              setSelectedUser(row.row);
              setOpenDrawer(true);
            }}
            circleColor={circleColor}
            selectedIdsToApprove={selectedIdsToApprove}
            setSelectedIdsToApprove={setSelectedIdsToApprove}
          />
          {userToPing && (
            <PingManagerModal
              open={openEmailModal}
              handleClose={() => setOpenEmailModal(false)}
              userAdminRow={userToPing}
            />
          )}
          <AdminUserDrawer
            openDrawer={openDrawer}
            onClose={() => setOpenDrawer(false)}
            selectedUser={selectedUser}
            adminUsers={adminUsers}
            setUserToPing={setUserToPing}
            setOpenModal={setOpenEmailModal}
            circleColor={circleColor}
            handleHeSystemConnect={handleHeSystemConnect}
          />
        </Box>
      )}
    </AppPage>
  );
};

export default AdminPermissionsView;
